import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, ArrowRight, Check, AlertCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 1600);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      <div className="ambient-bg" />
      <div className="w-full max-w-md p-8 animate-in">
        <div className="mb-8 flex flex-col items-center gap-4">
          <BrandLogo variant="icon" iconSize={44} as="div" testid="reset-brand" />
          <div className="text-center">
            <h1 className="text-2xl font-semibold tracking-tight text-zinc-50">Set a new password</h1>
            <p className="text-sm text-zinc-500 mt-1">Choose something you&apos;ll actually remember.</p>
          </div>
        </div>

        {!token ? (
          <div
            data-testid="reset-invalid"
            className="p-4 rounded-lg border border-rose-500/25 bg-rose-500/5 text-sm text-rose-200 flex items-start gap-2"
          >
            <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              This link is missing its reset token.{" "}
              <Link to="/forgot-password" className="underline">Request a new one</Link>.
            </div>
          </div>
        ) : done ? (
          <div
            data-testid="reset-success"
            className="p-5 rounded-xl border border-emerald-500/25 bg-emerald-500/5 text-sm text-emerald-200"
          >
            <Check className="h-4 w-4 inline mr-2 -mt-0.5" />
            Password updated. Redirecting you to sign in…
          </div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                New password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="reset-password-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
                  placeholder="At least 6 characters"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-zinc-500 mb-2">
                Confirm password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
                <input
                  data-testid="reset-confirm-input"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full bg-zinc-900/60 border border-zinc-800 rounded-lg pl-10 pr-3 py-2.5 text-sm text-zinc-50 placeholder:text-zinc-600 focus:border-zinc-700 focus:outline-none"
                  placeholder="Repeat password"
                />
              </div>
            </div>
            {error && (
              <div
                data-testid="reset-error"
                className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-lg px-3 py-2"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 text-amber-950 hover:bg-amber-400 disabled:opacity-60 font-semibold rounded-lg px-4 py-2.5 transition-colors"
            >
              {submitting ? "Updating…" : "Update password"}
              {!submitting && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>
        )}

        <div className="mt-6 text-center text-sm text-zinc-500">
          <Link to="/login" className="text-zinc-300 hover:text-white">
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
